import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { StatusDot } from '@/components/synapse/StatusDot';
import { expiryStatus, type ExpiryStatus, DEFAULT_EXPIRING_WITHIN_DAYS } from '@/lib/expiry';
import type { BatchWithBalance } from '@/types/api';

const TONE_BY_STATUS: Record<ExpiryStatus, 'ok' | 'warn' | 'danger' | 'neutral'> = {
  expired: 'danger',
  expiring_soon: 'warn',
  healthy: 'ok',
  no_expiry: 'neutral',
};

const TEXT_TONE_BY_STATUS: Record<ExpiryStatus, string> = {
  expired: 'text-destructive',
  expiring_soon: 'text-warn',
  healthy: 'text-muted',
  no_expiry: 'text-muted',
};

interface BatchRowProps {
  batch: BatchWithBalance;
  /** Window for "expiring soon" classification. Defaults to the screen-wide 60-day standard. */
  withinDays?: number;
  className?: string;
}

/**
 * Compact, single-row batch summary used wherever a batch's identity + expiry
 * + remaining quantity needs to be shown (Stock Status, Adjust, Issue draft).
 * Renders: status dot · batch number (mono) · expiry label (mono, colored) ·
 * balance (mono, right-aligned).
 */
export function BatchRow({
  batch,
  withinDays = DEFAULT_EXPIRING_WITHIN_DAYS,
  className,
}: BatchRowProps) {
  const { t } = useTranslation();
  const status = expiryStatus(batch.expiryDate, withinDays);
  return (
    <div className={cn('flex min-w-0 flex-1 items-center gap-2', className)}>
      <StatusDot tone={TONE_BY_STATUS[status]} />
      <span className="font-mono text-[12.5px] text-ink tabular-nums">
        {batch.batchNumber ?? '—'}
      </span>
      <span className={cn('font-mono text-[11.5px] tabular-nums', TEXT_TONE_BY_STATUS[status])}>
        {batch.expiryDate
          ? status === 'expired'
            ? t('adjust.expiredOn', { date: batch.expiryDate })
            : t('adjust.expDate', { date: batch.expiryDate })
          : t('issue.noExpiry')}
      </span>
      <span className="ml-auto font-mono text-[12px] tabular-nums text-muted">
        {t('issue.nOnHand', { count: batch.balance })}
      </span>
    </div>
  );
}
